/**
 * Componente MovieCard
 * Representa una tarjeta individual de película
 * 
 * - movie: object with {id, title, year, status_id, poster_path}
 * - statuses: array of available statuses
 * - onStatusChange: callback when status changes
 * - onDelete: callback cuando se elimina
 * - onRatingChange: callback cuando cambia la calificación
 * - user: objeto del usuario actual
 */

import React from 'react';
import { Film, Trash2 } from 'lucide-react';
import { tmdbApi } from '../../api/tmdb.js';
import { getRatingText } from '../../utils/ratingUtils.js';
import { getRelativeTime } from '../../utils/dateUtils.js';
import { StarRating } from '../common/StarRating.jsx';

const MovieCard = ({ movie, statuses, onStatusChange, onDelete, onRatingChange, user }) => {
  const posterUrl = tmdbApi.getPosterUrl(movie.poster_path);
  const [hoverRating, setHoverRating] = React.useState(0);
  const rating = movie.rating || 0;

  const pendingStatus = statuses?.find(s => s.description === 'Pending');
  const isPending = movie.status_id === pendingStatus?.id;

  // Handle click on star (detect left or right half)
  const handleStarClick = (starNumber, event) => {
    if (isPending) {
      alert('You can\'t rate a pending movie. Update its status first.');
      return;
    }

    if (!user) {
      alert('Sign in to rate movies');
      return;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const isLeftHalf = clickX < rect.width / 2;

    const newRating = isLeftHalf ? starNumber - 0.5 : starNumber;
    onRatingChange(movie.id, newRating);
  };

  // Handle hover for preview
  const handleStarHover = (starNumber, event) => {
    if (isPending) {
      setHoverRating(0);
      return;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const hoverX = event.clientX - rect.left;
    const isLeftHalf = hoverX < rect.width / 2;

    const hoverValue = isLeftHalf ? starNumber - 0.5 : starNumber;
    setHoverRating(hoverValue);
  };

  return (
    <div className="bg-slate-800 rounded-lg overflow-hidden group hover:ring-2 hover:ring-purple-500 transition">
      {/* Póster */}
      <div className="aspect-[2/3] bg-slate-700 relative">
        {posterUrl ? (
          <img
            src={posterUrl}
            alt={movie.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Si la imagen falla, mostrar placeholder
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        
        {/* Placeholder si no hay póster */}
        <div 
          className="w-full h-full flex items-center justify-center"
          style={{ display: posterUrl ? 'none' : 'flex' }}
        >
          <Film className="w-8 sm:w-12 h-8 sm:h-12 text-slate-600" />
        </div>

        {/* Botón de eliminar (aparece al hover) */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-70 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            onClick={() => onDelete(movie.id)}
            className="p-1.5 sm:p-2 bg-red-600 hover:bg-red-700 rounded-full transition"
            title="Delete movie"
          >
            <Trash2 className="w-3 sm:w-4 h-3 sm:h-4 text-white" />
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-2 sm:p-3">
        <h3 
          className="text-white font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1 truncate" 
          title={movie.title}
        >
          {movie.title}
        </h3>

        {movie.year && (
          <div className="text-slate-400 text-xs mb-1 sm:mb-2">{movie.year}</div>
        )}

        {/* Updated time */}
        <p className="text-slate-500 text-xs mb-1 sm:mb-2">Updated {getRelativeTime(movie.updated_at)}</p>

        {/* Rating with stars */}
        <div className="mb-1.5 sm:mb-2 pb-1.5 sm:pb-2 border-b border-slate-700 min-h-[2.5rem] sm:min-h-[3rem]">
          <StarRating
            rating={rating}
            hoverRating={hoverRating}
            isPending={isPending}
            onStarClick={handleStarClick}
            onStarHover={handleStarHover}
            onMouseLeave={() => setHoverRating(0)}
            showText={true}
            pendingMessage="Rate when you watch it"
          />
        </div>

        {/* Status selector */}
        <select
          value={movie.status_id}
          onChange={(e) => onStatusChange(movie.id, parseInt(e.target.value))}
          className="w-full bg-slate-700 text-white text-xs px-1.5 sm:px-2 py-1 sm:py-1.5 rounded cursor-pointer hover:bg-slate-600 transition"
        >
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.description}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default MovieCard;
